import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCart } from "../../context/CartContext";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:89").replace(
  /\/$/,
  ""
);

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const PAYPAL_CURRENCY = "MXN";
const PAYPAL_LOCALE = "es_MX";

const loadPayPalScript = () =>
  new Promise((resolve, reject) => {
    if (window.paypal) {
      resolve();
      return;
    }

    const existing = document.getElementById("paypal-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("PayPal no cargó.")));
      return;
    }

    if (!PAYPAL_CLIENT_ID) {
      reject(new Error("Falta REACT_APP_PAYPAL_CLIENT_ID"));
      return;
    }

    const script = document.createElement("script");
    script.id = "paypal-sdk";
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=${PAYPAL_CURRENCY}&locale=${PAYPAL_LOCALE}&intent=capture`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("PayPal no cargó."));
    document.body.appendChild(script);
  });

const PayPalCheckoutButton = ({ customer, onSuccess }) => {
  const paypalRef = useRef(null);
  const createOrderPromiseRef = useRef(null);
  const customerRef = useRef(customer);
  const onSuccessRef = useRef(onSuccess);
  const { items, totalUnits, minimumOrderUnits, clearCart, closeCart } = useCart();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const isMinimumOrderMet = totalUnits >= minimumOrderUnits;

  const itemsKey = useMemo(
    () => items.map((item) => `${item.key}:${item.quantity}`).join("|"),
    [items]
  );

  useEffect(() => {
    customerRef.current = customer;
  }, [customer]);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    let isMounted = true;

    loadPayPalScript()
      .then(() => {
        if (!isMounted) return;
        setLoading(false);

        if (!paypalRef.current) return;
        paypalRef.current.innerHTML = "";

        window.paypal
          .Buttons({
            createOrder: async () => {
              if (createOrderPromiseRef.current) {
                return createOrderPromiseRef.current;
              }
              setError("");
              if (!isMinimumOrderMet) {
                const message = `El pedido mínimo es de ${minimumOrderUnits} unidades en total.`;
                setError(message);
                throw new Error(message);
              }
              const promise = fetch(`${API_BASE_URL}/api/ecommerce/paypal/order`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  items: items.map((item) => ({
                    type: item.type,
                    quantity: item.quantity,
                    payload: item.payload,
                    name: item.name,
                  })),
                  customer: customerRef.current,
                }),
              })
                .then(async (response) => {
                  const contentType = response.headers.get("content-type") || "";
                  const text = await response.text();
                  let data = {};
                  if (contentType.includes("application/json")) {
                    data = JSON.parse(text);
                  }
                  if (!response.ok) {
                    if (data && typeof data === "object") {
                      console.error("create order error payload:", data);
                    }
                    throw new Error(data.message || "No se pudo crear la orden en PayPal.");
                  }
                  if (!contentType.includes("application/json")) {
                    throw new Error("No se pudo crear la orden en PayPal.");
                  }
                  if (!data.paypal_order_id) {
                    throw new Error("PayPal no devolvió la orden.");
                  }
                  return data.paypal_order_id;
                })
                .finally(() => {
                  createOrderPromiseRef.current = null;
                });

              createOrderPromiseRef.current = promise;
              return promise;
            },
            onApprove: async (data) => {
              setError("");
              const response = await fetch(`${API_BASE_URL}/api/ecommerce/paypal/capture`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  paypal_order_id: data.orderID,
                }),
              });
              const contentType = response.headers.get("content-type") || "";
              const text = await response.text();
              let result = {};
              if (contentType.includes("application/json")) {
                result = JSON.parse(text);
              }
              if (!response.ok) {
                throw new Error(result.message || "No se pudo capturar el pago.");
              }
              if (!contentType.includes("application/json")) {
                throw new Error("No se pudo capturar el pago.");
              }
              if (result.status !== "COMPLETED") {
                throw new Error("El pago no se completó.");
              }

              clearCart();
              closeCart();
              if (onSuccessRef.current) onSuccessRef.current();
            },
            onError: (err) => {
              console.error(err);
              setError("Transacción no exitosa.");
            },
          })
          .render(paypalRef.current);
      })
      .catch((err) => {
        if (!isMounted) return;
        setLoading(false);
        setError(err.message || "No se pudo cargar PayPal.");
      });

    return () => {
      isMounted = false;
    };
  }, [itemsKey, clearCart, closeCart, items, isMinimumOrderMet, minimumOrderUnits]);

  if (!items.length) return null;
  if (!isMinimumOrderMet) {
    return (
      <div className="pt-3 text-xs font-semibold text-amber-300">
        El pedido mínimo es de {minimumOrderUnits} unidades en total.
      </div>
    );
  }

  return (
    <div className="pt-3">
      {loading && <div className="text-xs text-white/60">Cargando PayPal...</div>}
      {error && <div className="text-xs text-red-400 font-semibold">{error}</div>}
      <div ref={paypalRef} />
    </div>
  );
};

export default PayPalCheckoutButton;
